/**
 * Field-query parsing + per-seat board snapshots.
 *
 * The engine calls OCG_DuelQueryLocation per zone (see apps/engine/src/core/snapshot.ts) and feeds
 * the raw buffers here. `parseQueryLocation` turns one zone's buffer into cards; `maskBoardForSeat`
 * produces the view a given player is allowed to see (opponent hand / face-down codes stripped).
 *
 * Query-buffer layout (OCGcore v11.0, card::get_infos / OCG_DuelQueryLocation):
 *   [u32 totalSize] then per zone-slot: either [u16 0] (empty slot) or a run of field blocks
 *   [u16 blockLen][u32 flag][value(blockLen-4)] terminated by a [u16 4][u32 QUERY_END] block.
 */
import { QUERY } from "./messages.js";
/** Recommended query flags for a renderable snapshot (code is masked per-seat afterwards). */
export const SNAPSHOT_FLAGS = QUERY.CODE | QUERY.POSITION | QUERY.TYPE | QUERY.LEVEL | QUERY.RANK |
    QUERY.ATTRIBUTE | QUERY.ATTACK | QUERY.DEFENSE | QUERY.LINK | QUERY.IS_PUBLIC |
    QUERY.OVERLAY_CARD | QUERY.COUNTERS | QUERY.EQUIP_CARD | QUERY.TARGET_CARD;
/** Parse one zone's OCG_DuelQueryLocation buffer. Returns slot order; empty zone slots are null. */
export function parseQueryLocation(buf, controller, location) {
    const out = [];
    if (buf.length < 4)
        return out;
    const dv = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
    let off = 0;
    const total = dv.getUint32(off, true);
    off += 4;
    const end = off + total;
    let seq = 0;
    while (off < end) {
        let len = dv.getUint16(off, true);
        off += 2;
        if (len === 0) {
            out.push(null);
            seq++;
            continue;
        }
        const card = { controller, location, sequence: seq, code: 0, position: 0 };
        for (;;) {
            const flag = dv.getUint32(off, true);
            off += 4;
            if (flag === QUERY.END)
                break;
            const v = off; // value start; value is (len - 4) bytes
            switch (flag) {
                case QUERY.CODE:
                    card.code = dv.getUint32(v, true);
                    break;
                case QUERY.POSITION:
                    card.position = dv.getUint32(v, true);
                    break;
                case QUERY.TYPE:
                    card.type = dv.getUint32(v, true);
                    break;
                case QUERY.LEVEL:
                    card.level = dv.getUint32(v, true);
                    break;
                case QUERY.RANK:
                    card.rank = dv.getUint32(v, true);
                    break;
                case QUERY.ATTRIBUTE:
                    card.attribute = dv.getUint32(v, true);
                    break;
                case QUERY.ATTACK:
                    card.attack = dv.getInt32(v, true);
                    break;
                case QUERY.DEFENSE:
                    card.defense = dv.getInt32(v, true);
                    break;
                case QUERY.IS_PUBLIC:
                    card.isPublic = dv.getUint8(v) !== 0;
                    break;
                case QUERY.LINK:
                    card.link = dv.getUint32(v, true);
                    card.linkMarker = dv.getUint32(v + 4, true);
                    break;
                case QUERY.OVERLAY_CARD:
                    card.overlayCount = dv.getUint32(v, true);
                    break;
                case QUERY.COUNTERS: {
                    // [u32 typeCount] then typeCount × [u32 = counterType | (total << 16)] (card.cpp QUERY_COUNTERS).
                    const n = dv.getUint32(v, true);
                    if (n > 0) {
                        const counters = {};
                        for (let k = 0; k < n; k++) {
                            const raw = dv.getUint32(v + 4 + k * 4, true);
                            counters[raw & 0xffff] = (raw >>> 16) & 0xffff;
                        }
                        card.counters = counters;
                    }
                    break;
                }
                case QUERY.EQUIP_CARD: {
                    // [u8 ctrl][u8 loc][u32 seq][u32 pos] — loc 0 = not equipped (card.cpp QUERY_EQUIP_CARD).
                    const loc = dv.getUint8(v + 1);
                    if (loc !== 0)
                        card.equip = { controller: dv.getUint8(v), location: loc, sequence: dv.getUint32(v + 2, true) };
                    break;
                }
                case QUERY.TARGET_CARD: {
                    // [u32 count] then count × [u8 ctrl][u8 loc][u32 seq][u32 pos] (card.cpp QUERY_TARGET_CARD).
                    const n = dv.getUint32(v, true);
                    const targets = [];
                    for (let k = 0, o = v + 4; k < n; k++, o += 10) {
                        const loc = dv.getUint8(o + 1);
                        if (loc !== 0)
                            targets.push({ controller: dv.getUint8(o), location: loc, sequence: dv.getUint32(o + 2, true) });
                    }
                    if (targets.length)
                        card.targets = targets;
                    break;
                }
                default: break; // unknown field — skip its bytes
            }
            off += len - 4;
            len = dv.getUint16(off, true);
            off += 2; // next block's length
        }
        out.push(card);
        seq++;
    }
    return out;
}
/** The face-up card on top of the Extra Deck — a Pendulum monster returned there sits face-up and is
 *  public; the face-down Extra-Deck stack shows a card back (code 0). Picks the last face-up card. */
export function extraTopCode(p) {
    for (let i = p.extra.length - 1; i >= 0; i--) {
        const c = p.extra[i];
        if (c && c.code && (c.position & 0x5) !== 0)
            return c.code; // 0x5 = POS_FACEUP (attack | defense)
    }
    return 0;
}
/** A card is visible to `seat` if the core marked it public, or the seat controls it. */
const visibleTo = (c, seat) => !!c.isPublic || c.controller === seat;
/** Strip a hidden card down to position-only (a face-down "back" the client can render). */
const maskCard = (c, seat) => visibleTo(c, seat) ? c
    : { controller: c.controller, location: c.location, sequence: c.sequence, code: 0, position: c.position, isPublic: false, overlayCount: c.overlayCount, counters: c.counters, equip: c.equip, targets: c.targets };
const maskList = (list, seat) => list.map((c) => (c ? maskCard(c, seat) : c));
/** Return the board as `seat` is allowed to see it (opponent hand + face-down codes removed). */
export function maskBoardForSeat(board, seat) {
    const maskP = (p) => ({
        ...p,
        mzone: maskList(p.mzone, seat),
        szone: maskList(p.szone, seat),
        hand: maskList(p.hand, seat),
        grave: maskList(p.grave, seat), // grave is public; pass-through, but cheap to run
        removed: maskList(p.removed, seat),
        extra: maskList(p.extra, seat), // owner sees all; opponent sees only face-up (public) Pendulums
    });
    return { ...board, players: [maskP(board.players[0]), maskP(board.players[1])] };
}
//# sourceMappingURL=field.js.map